import { useDispatch } from 'react-redux';
import { isNumber, join } from 'lodash/fp';
import { reset } from 'redux-form';
import { useCallback } from 'react';
import { FieldArrayWithId } from 'react-hook-form';
import { lt, showInfoAlert, useDateTimeLocalizationInfo, useTimezone } from '@mews/framework';
import { getDateTimeUtc, getEarliestDayTime, utcToLocalizedDate, useCommanderSelector } from '@mews-commander/utils';
import { useService } from '@mews-commander/legacy-entities/src/utils/useService';
import { isBookableService } from '@mews-commander/legacy-entities/src/ducks/servicesNew';
import {
    getBulkUpdateAllowedDateTimeUnitData,
    getResourceCategoryAvailabilities,
    sumBlockPickUpValue,
} from '../../../selectors';
import { AvailabilityAdjustmentFormValues } from '../../../types';
import { EditDialog } from '../../../enums';
import { useBlockSetupContext } from '../../../contexts/blockSetupContext';
import { BulkAdjustAvailabilitiesFormValues } from './types';

export const useBulkUpdate = (
    serviceId: string,
    rowIndex: number,
    availabilityBlockId: string,
    watchedResourceCategoryId: string,
    resourceCategoryField: FieldArrayWithId<AvailabilityAdjustmentFormValues, 'fieldArrayAvailability'>,
    onClose: () => void
) => {
    const dispatch = useDispatch();
    const timezone = useTimezone();

    const resourceCategoryAvailabilities = useCommanderSelector(getResourceCategoryAvailabilities);
    const localizationInfo = useDateTimeLocalizationInfo();
    const service = useService(serviceId);
    const timeUnit = service && isBookableService(service) ? service.data.value?.timeUnitPeriod : undefined;

    const { updateRow } = useBlockSetupContext();

    const dateTimeUnitIndexParser = getBulkUpdateAllowedDateTimeUnitData(timeUnit)?.dateTimeUnitIndexParser;
    const time = getEarliestDayTime(localizationInfo);

    return useCallback(
        (values: BulkAdjustAvailabilitiesFormValues) => {
            const datesUnderReservationCapacity: string[] = [];
            const { allowedDateTimeUnitIndexes } = values;
            const startUtc = getDateTimeUtc({
                timezone,
                localizationInfo,
                // date type is wrong since form validation does not allow date to be 'null'
                date: values.startDate as string,
                time: values.startHour || time,
            });

            const endUtc = getDateTimeUtc({
                timezone,
                localizationInfo,
                // date type is wrong since form validation does not allow date to be 'null'
                date: values.endDate as string,
                time: values.endHour || time,
            });

            const finalResourceCategoryId = resourceCategoryField.resourceCategoryId || watchedResourceCategoryId;

            const newRowValue = {
                ...resourceCategoryField,
                resourceCategoryId: finalResourceCategoryId,
                availabilities: resourceCategoryField.availabilities.map((availability) => {
                    const { dateTimeUtcForm } = availability;

                    if (
                        startUtc <= dateTimeUtcForm &&
                        endUtc >= dateTimeUtcForm &&
                        (!dateTimeUnitIndexParser ||
                            !allowedDateTimeUnitIndexes ||
                            allowedDateTimeUnitIndexes.includes(dateTimeUnitIndexParser(dateTimeUtcForm, timezone)))
                    ) {
                        const pickedUp = sumBlockPickUpValue(
                            resourceCategoryAvailabilities,
                            dateTimeUtcForm,
                            finalResourceCategoryId,
                            availabilityBlockId
                        );

                        let valueAmount = values.amount;

                        if (isNumber(valueAmount) && pickedUp > valueAmount) {
                            valueAmount = pickedUp;
                            datesUnderReservationCapacity.push(utcToLocalizedDate(dateTimeUtcForm, timezone));
                        }

                        return {
                            ...availability,
                            adjustmentValue: { ...availability.adjustmentValue, value: valueAmount as string },
                        };
                    }
                    return availability;
                }),
            };

            updateRow(rowIndex, newRowValue);

            if (datesUnderReservationCapacity.length) {
                const dates = join(', \n', datesUnderReservationCapacity);
                showInfoAlert({
                    description: lt('AvailabilitiesAutomaticUpdateWarningMessage', { dates: `\n ${dates}.` }),
                });
            }

            onClose();
            return dispatch(reset(EditDialog.BulkAdjustAvailabilities));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [watchedResourceCategoryId]
    );
};
